/**
 * FormSubmission Integration — survey responses hub + generic CRUD helpers.
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { useSurveyResponsesStore } from '@/store/survey-responses.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class FormSubmissionIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchSurveyResponses(params = {}) {
        const store = useSurveyResponsesStore.getState();
        store.setLoading(true);
        store.setError(null);
        try {
            const response = await this.service.getSurveyResponses(params);
            const items = response.data || [];
            store.setItems(items);
            store.setPaginationData({
                currentPage: response.page || params.page || 1,
                totalPages: response.totalPages || 1,
                totalItems: response.total || 0,
                itemsPerPage: response.pageSize || params.pageSize || 15,
                hasNextPage: (response.page || 1) < (response.totalPages || 1),
                hasPreviousPage: (response.page || 1) > 1,
            });
            store.setUniqueValues(response.uniqueValues || {});
            return { success: true, data: items };
        } catch (error) {
            const message = getIntegrationErrorMessage(error)
                || i18next.t('definitionsSurveyResponses:messages.loadError', {
                    defaultValue: 'Failed to load survey responses',
                });
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchSurveyResponseById(id) {
        const store = useSurveyResponsesStore.getState();
        store.setDetailLoading(true);
        try {
            const response = await this.service.getSurveyResponseById(id);
            store.setSelectedItem(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = getIntegrationErrorMessage(error)
                || i18next.t('definitionsSurveyResponses:messages.loadDetailError', {
                    defaultValue: 'Failed to load survey response',
                });
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setDetailLoading(false);
        }
    }

    async fetchAll(params = {}) {
        try {
            const response = await this.service.getAll(params);
            return { success: true, data: response.data };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async getById(id) {
        try {
            const response = await this.service.getById(id);
            return { success: true, data: response.data };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async submitVendorSurvey({ formId, data, vendorAssessmentId }) {
        try {
            const response = await this.service.createSubmit({
                formId,
                data,
                vendorAssessmentId,
            });
            const submission = response?.data?.data ?? response?.data;
            toast.success(i18next.t('common:messages.saveSuccess', { defaultValue: 'Saved' }));
            return { success: true, data: submission };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error)
                || i18next.t('integrationToasts:submissionFailed', { defaultValue: 'Submission failed' });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async create(data) {
        try {
            const response = await this.service.create(data);
            toast.success(
                i18next.t('integrationToasts:createdSuccessfully', { defaultValue: 'Created successfully' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async update(id, data) {
        try {
            const response = await this.service.update(id, data);
            toast.success(
                i18next.t('integrationToasts:updatedSuccessfully', { defaultValue: 'Updated successfully' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async delete(id) {
        try {
            await this.service.delete(id);
            toast.success(
                i18next.t('integrationToasts:deletedSuccessfully', { defaultValue: 'Deleted successfully' }),
            );
            return { success: true };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }
}
